import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';

function parsePlace(place) {
  const components = place.address_components || [];
  const get = (type) => {
    const match = components.find((c) => (c.types || []).includes(type));
    return match ? match.long_name : '';
  };
  const street = [get('street_number'), get('route')].filter(Boolean).join(' ');
  return {
    address1: street || place.name || '',
    city: get('locality') || get('postal_town') || get('administrative_area_level_2') || '',
    state: get('administrative_area_level_1') || '',
    postal_code: get('postal_code') || '',
    country: get('country') || '',
    formatted_address: place.formatted_address || '',
    place_id: place.place_id || '',
    lat: place.geometry?.location?.lat?.() ?? null,
    lng: place.geometry?.location?.lng?.() ?? null,
  };
}

function loadGoogleMaps(apiKey) {
  if (!apiKey) return Promise.reject(new Error('Missing Google Maps API key'));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  const existing = document.getElementById('google-maps-places-script');
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.google?.maps?.places) {
        resolve(window.google);
        return;
      }
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'google-maps-places-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const GooglePlacesAddressInput = ({
  apiKey,
  value = '',
  onChange,
  placeholder = 'Search address on Google Maps…',
  className = '',
  theme = 'dark',
}) => {
  const dash = theme === 'dash';
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (inputRef.current && value != null && document.activeElement !== inputRef.current) {
      inputRef.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    if (!apiKey || !inputRef.current) {
      setReady(false);
      return undefined;
    }
    let cancelled = false;
    let listener = null;

    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        if (autocompleteRef.current && window.google?.maps?.event) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name'],
        });
        listener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (!place || !place.geometry) return;
          const parsed = parsePlace(place);
          const formatted = parsed.formatted_address || inputRef.current?.value || '';
          if (inputRef.current) inputRef.current.value = formatted;
          onChangeRef.current?.(formatted, parsed);
        });
        setReady(true);
        setError('');
      })
      .catch(() => {
        if (!cancelled) {
          setReady(false);
          setError('Google Maps could not load. Check GOOGLE_MAPS_API_KEY in Settings → API Keys.');
        }
      });

    return () => {
      cancelled = true;
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [apiKey]);

  return (
    <div className="space-y-1">
      <div className="relative">
        <MapPin className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${dash ? 'text-slate-400' : 'text-zinc-500'}`} />
        <input
          ref={inputRef}
          type="text"
          defaultValue={value}
          onChange={(e) => onChangeRef.current?.(e.target.value, { formatted_address: e.target.value })}
          placeholder={placeholder}
          className={`w-full pl-10 pr-3 py-2 rounded-lg ${
            dash
              ? 'bg-white border border-slate-300 text-slate-900'
              : 'bg-zinc-800 border border-zinc-700'
          } ${className}`}
        />
      </div>
      {apiKey && ready && (
        <p className={`text-xs ${dash ? 'text-emerald-600' : 'text-emerald-400'}`}>Pick an address from Google suggestions — coordinates sync when you save.</p>
      )}
      {!apiKey && (
        <p className={`text-xs ${dash ? 'text-amber-700' : 'text-amber-400'}`}>Add GOOGLE_MAPS_API_KEY in Settings → API Keys for Google address search.</p>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

export default GooglePlacesAddressInput;
